import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Divider,
  Alert,
} from "@mui/material";
import axios from "axios";
import { host } from "../constant/backendpath";

function AssignMentor({ profileDetails }) {
  const [projectYear, setProjectYear] = useState(
    new Date().getFullYear()
  );

  const [teams, setTeams] = useState([]);
  const [mentors, setMentors] = useState([]);

  const [selectedTeam, setSelectedTeam] = useState("");
  const [mentor1, setMentor1] = useState("");
  const [mentor2, setMentor2] = useState("");
  const [alumnimentor, setAlumnimentor] = useState("");

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // =========================
  // FETCH TEAMS
  // =========================
  const fetchTeams = async () => {
    try {
      setError("");

      const res = await axios.get(
        `${host}project?projectYear=${projectYear}`,
        {
          withCredentials: true,
        }
      );

      setTeams(res.data);
    } catch (err) {
      console.error("Error fetching teams:", err);
      setTeams([]);
      setError("Unable to fetch teams");
    }
  };

  // =========================
  // FETCH MENTORS
  // =========================
  const fetchMentors = async () => {
    try {
      const res = await axios.get(`${host}mentors`, {
        withCredentials: true,
      });

      setMentors(res.data);
    } catch (err) {
      console.error("Error fetching mentors:", err);
      setMentors([]);
      setError("Unable to fetch mentors");
    }
  };

  // Fetch teams whenever year changes
  useEffect(() => {
    setSelectedTeam("");
    setMentor1("");
    setMentor2("");
    setAlumnimentor("");

    fetchTeams();
  }, [projectYear]);

  // Fetch mentors once
  useEffect(() => {
    fetchMentors();
  }, []);

  // =========================
  // TEAM CHANGE
  // =========================
  const handleTeamChange = (e) => {
    const teamId = e.target.value;

    setSelectedTeam(teamId);
    setMessage("");
    setError("");

    const team = teams.find(
      (item) => String(item.teamId) === String(teamId)
    );

   if (team) {
  setMentor1(team.mentor1?._id || "");
  setMentor2(team.mentor2?._id || "");
  setAlumnimentor(team.alumnimentor || "");
}
  };

  // =========================
  // SUBMIT
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();

    setMessage("");
    setError("");

    if (!selectedTeam) {
      setError("Please select a team");
      return;
    }

    if (!mentor1) {
      setError("Please select Mentor 1");
      return;
    }

    try {
      const res = await axios.put(
        `${host}assign-mentor`,
        {
          teamId: selectedTeam,
          mentor1,
          mentor2,
          alumnimentor,
        },
        {
          withCredentials: true,
        }
      );

      if (res.status === 200) {
        setMessage("Mentors assigned successfully");

        // Refresh teams
        await fetchTeams();
      }
    } catch (err) {
      console.error("Assign mentor error:", err);

      setError(
        err.response?.data?.msg ||
          "Failed to assign mentors"
      );
    }
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 60px)",
        backgroundColor: "#f5f7fa",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        p: {
          xs: 2,
          sm: 3,
          md: 5,
        },
      }}
    >
      <Paper
        elevation={4}
        sx={{
          width: "100%",
          maxWidth: "800px",
          p: {
            xs: 3,
            sm: 4,
          },
          borderRadius: "16px",
        }}
      >
        {/* HEADER */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#1f2937",
            }}
          >
            Assign Mentors
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b7280",
              mt: 0.5,
            }}
          >
            Welcome, {profileDetails?.name || "Admin"}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* SUCCESS MESSAGE */}
        {message && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
          >
            {message}
          </Alert>
        )}

        {/* ERROR MESSAGE */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
        >
          <Stack spacing={3}>

            {/* PROJECT YEAR */}
            <TextField
              select
              fullWidth
              label="Project Year"
              value={projectYear}
              onChange={(e) =>
                setProjectYear(Number(e.target.value))
              }
            >
              <MenuItem value={2026}>
                2026
              </MenuItem>

              <MenuItem value={2025}>
                2025
              </MenuItem>

              <MenuItem value={2024}>
                2024
              </MenuItem>

              <MenuItem value={2023}>
                2023
              </MenuItem>
            </TextField>

            {/* TEAM */}
            <TextField
              select
              fullWidth
              label="Select Team"
              value={selectedTeam}
              onChange={handleTeamChange}
            >
              {teams.length === 0 ? (
                <MenuItem disabled>
                  No teams found
                </MenuItem>
              ) : (
                teams.map((team) => (
                  <MenuItem
                    key={team.teamId}
                    value={team.teamId}
                  >
                    {team.teamLeadName} - {team.reg_no} ({team.dept})
                  </MenuItem>
                ))
              )}
            </TextField>

            {/* MENTORS */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
                gap: 2,
              }}
            >
              {/* MENTOR 1 */}
              <TextField
                select
                fullWidth
                label="Mentor 1"
                value={mentor1}
                onChange={(e) =>
                  setMentor1(e.target.value)
                }
              >
                {mentors.length === 0 ? (
                  <MenuItem disabled>
                    No mentors found
                  </MenuItem>
                ) : (
                  mentors.map((mentor) => (
                    <MenuItem
                      key={mentor._id}
                      value={mentor._id}
                    >
                      {mentor.name}
                      {mentor.dept
                        ? ` - ${mentor.dept}`
                        : ""}
                    </MenuItem>
                  ))
                )}
              </TextField>

              {/* MENTOR 2 */}
              <TextField
                select
                fullWidth
                label="Mentor 2"
                value={mentor2}
                onChange={(e) =>
                  setMentor2(e.target.value)
                }
              >
                <MenuItem value="">
                  None
                </MenuItem>

                {mentors.map((mentor) => (
                  <MenuItem
                    key={mentor._id}
                    value={mentor._id}
                  >
                    {mentor.name}
                    {mentor.dept
                      ? ` - ${mentor.dept}`
                      : ""}
                  </MenuItem>
                ))}
              </TextField>

              {/* ALUMNI MENTOR */}
              <TextField
                fullWidth
                label="Alumni Mentor"
                value={alumnimentor}
                onChange={(e) =>
                  setAlumnimentor(e.target.value)
                }
                placeholder="Enter alumni mentor"
              />
            </Box>

            {/* SUBMIT */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={!selectedTeam || !mentor1}
              sx={{
                backgroundColor: "#0797F3",
                borderRadius: "10px",
                py: 1.3,
                fontWeight: 600,
                fontSize: "16px",
                textTransform: "none",

                "&:hover": {
                  backgroundColor: "#057dcc",
                },

                "&:disabled": {
                  backgroundColor: "#bdbdbd",
                },
              }}
            >
              Assign Mentors
            </Button>

          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}

export default AssignMentor;